/**
 * Screen Share View Component
 *
 * Displays a remote user's shared screen
 * Separated from VoiceChannelWindow to fix React Hooks rules violation
 */

import React, { useRef, useEffect } from 'react';
import { Monitor } from 'lucide-react';
import { RemoteTrack } from 'livekit-client';

interface ScreenShareViewProps {
  participantId: string;
  participantName: string;
  track: RemoteTrack;
}

export const ScreenShareView: React.FC<ScreenShareViewProps> = ({
  participantId,
  participantName,
  track,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const videoEl = videoRef.current;
    if (videoEl && track) {
      track.attach(videoEl);
      videoEl.play().catch((err) => {
        console.error('[ScreenShare] Failed to play:', err);
      });
    }

    return () => {
      if (videoEl && track) {
        track.detach(videoEl);
      }
    };
  }, [track]);

  return (
    <div key={participantId} className="bg-black rounded overflow-hidden">
      <div className="p-2 bg-gray-700 flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Monitor className="w-4 h-4 text-blue-400" />
          <span className="text-sm font-medium">{participantName}</span>
        </div>
      </div>
      <div className="aspect-video w-full bg-black">
        <video
          ref={videoRef}
          autoPlay
          playsInline
          className="w-full h-full object-contain"
        />
      </div>
    </div>
  );
};
